import { firstNoteFrom, type MeasureInfo, type Timeline, type TimelineNote, type VoicePart, VOICE_PARTS } from '@corocanto/scoring';
import { type Channel, DEFAULT_LEVEL, MINE_LOUDER_LEVEL, type Mixer, OTHERS_WHEN_MINE_LOUDER, withoutSolo } from './mix';

/**
 * Rehearsal mode (wireframe 07): playback for a room, run by any member or the director. Pure
 * logic here; the page and the engine do the rest.
 */

/**
 * Rehearsal presets: every part at the normal level, or one voice part in front, for a section
 * working on its part with the others around it.
 */
export type PartFocus = 'all' | VoicePart;

/** The presets for a song: "all", then one per voice part the song has. */
export function partFocuses(songParts: readonly VoicePart[]): PartFocus[] {
  return ['all', ...songParts];
}

/**
 * Apply a preset; it unmutes every channel and clears every solo. "All" sets every channel to the
 * normal level. A part in front gets the practice page's "all, mine louder" levels: that part at
 * the top, the other voices and the accompaniment well below it.
 */
export function applyFocus(focus: PartFocus, mixer: Mixer): Mixer {
  const next = withoutSolo(mixer);
  for (const ch of Object.keys(next) as Channel[]) next[ch] = { ...next[ch], muted: false };
  for (const p of VOICE_PARTS) {
    const volume = focus === 'all' ? DEFAULT_LEVEL : p === focus ? MINE_LOUDER_LEVEL : OTHERS_WHEN_MINE_LOUDER;
    next[p] = { ...next[p], volume };
  }
  next.acc = { ...next.acc, volume: focus === 'all' ? DEFAULT_LEVEL : OTHERS_WHEN_MINE_LOUDER };
  return next;
}

/** The preset the mixer matches, or null (a mute, a solo or a level set by hand). Only the song's own channels count. */
export function matchingFocus(mixer: Mixer, songParts: readonly VoicePart[], hasAccompaniment: boolean): PartFocus | null {
  const channels: Channel[] = [...songParts, ...(hasAccompaniment ? (['acc'] as const) : [])];
  if (channels.some((ch) => mixer[ch].muted || mixer[ch].solo)) return null;
  if (channels.every((ch) => mixer[ch].volume === DEFAULT_LEVEL)) return 'all';
  return (
    songParts.find((front) =>
      channels.every((ch) => mixer[ch].volume === (ch === front ? MINE_LOUDER_LEVEL : OTHERS_WHEN_MINE_LOUDER)),
    ) ?? null
  );
}

/** Each part's starting note from the start of a timeline bar: its first note there or after. */
export function startingNotes(timeline: Timeline, timelineMeasure: number, parts: readonly VoicePart[]): Partial<Record<VoicePart, TimelineNote>> {
  const sec = timeline.measures[timelineMeasure]?.startSec ?? 0;
  const out: Partial<Record<VoicePart, TimelineNote>> = {};
  for (const p of parts) {
    const note = firstNoteFrom(timeline, p, sec);
    if (note) out[p] = note;
  }
  return out;
}

/** A rehearsal mark and the written bar it is in. */
export interface RehearsalMark {
  label: string;
  measureIndex: number;
}

export function rehearsalMarks(measures: readonly MeasureInfo[]): RehearsalMark[] {
  return measures.flatMap((m, measureIndex) => m.rehearsalMarks.map((label) => ({ label, measureIndex })));
}

/** The rehearsal mark of the section a timeline bar is in: the last mark at or before it, in playback order. */
export function markAt(timeline: Timeline, measures: readonly MeasureInfo[], timelineMeasure: number): string | null {
  for (let i = Math.min(timelineMeasure, timeline.measures.length - 1); i >= 0; i--) {
    const m = timeline.measures[i];
    const marks = m ? measures[m.measureIndex]?.rehearsalMarks : undefined;
    if (marks && marks.length > 0) return marks[marks.length - 1] as string;
  }
  return null;
}

/** The first time a written bar plays, by its printed number ("12", "12a"); null if there is no such bar. */
export function timelineIndexOfNumber(timeline: Timeline, measureNumber: string): number | null {
  const wanted = measureNumber.trim();
  const i = timeline.measures.findIndex((m) => m.measureNumber === wanted);
  return i < 0 ? null : i;
}

/** The first time a written bar plays, by its index. */
export function timelineIndexOfMeasure(timeline: Timeline, measureIndex: number): number | null {
  const i = timeline.measures.findIndex((m) => m.measureIndex === measureIndex);
  return i < 0 ? null : i;
}

/** Keyboard shortcuts 1–5 toggle these channels, in this order. */
export const SHORTCUT_CHANNELS: readonly Channel[] = ['S', 'A', 'T', 'B', 'acc'];
